import { Plus, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { ShippingRate, ShippingZone } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteShippingDialog } from '../components/delete-shipping-dialog';
import { RateDialog } from '../components/rate-dialog';
import type { RateAction } from '../components/rates-table';
import { ZoneCard, type ZoneAction } from '../components/zone-card';
import { ZoneDialog } from '../components/zone-dialog';
import { useDeleteRate, useDeleteZone, useUpdateRate } from '../hooks/use-shipping-mutations';
import { useShippingZones } from '../hooks/use-shipping-zones';

type Dialog =
  | { kind: 'none' }
  | { kind: 'zone'; zone: ShippingZone | null }
  | { kind: 'deleteZone'; zone: ShippingZone }
  | { kind: 'rate'; zone: ShippingZone; rate: ShippingRate | null }
  | { kind: 'deleteRate'; zone: ShippingZone; rate: ShippingRate };

function Shipping() {
  const { t } = useTranslation('shipping');
  const store = useStore();
  const query = useShippingZones(store.id);
  const updateRate = useUpdateRate(store.id);
  const deleteZone = useDeleteZone(store.id);
  const deleteRate = useDeleteRate(store.id);
  const message = useErrorMessage();
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const zones = query.data ?? [];
  const closeDialog = (open: boolean) => {
    if (!open) setDialog({ kind: 'none' });
  };

  const onZoneAction = (action: ZoneAction, zone: ShippingZone) => {
    switch (action) {
      case 'edit':
        setDialog({ kind: 'zone', zone });
        return;
      case 'addRate':
        setDialog({ kind: 'rate', zone, rate: null });
        return;
      case 'delete':
        setDialog({ kind: 'deleteZone', zone });
    }
  };

  const onRateAction = (action: RateAction, rate: ShippingRate, zone: ShippingZone) => {
    switch (action) {
      case 'edit':
        setDialog({ kind: 'rate', zone, rate });
        return;
      case 'activate':
      case 'deactivate':
        updateRate.mutate(
          { id: rate.id, body: { isActive: action === 'activate' } },
          { onError: (error) => toast.error(message(error)) },
        );
        return;
      case 'delete':
        setDialog({ kind: 'deleteRate', zone, rate });
    }
  };

  const addButton = (tour?: 'settings.shipping.add-zone') =>
    editable ? (
      <Button data-tour={tour} onClick={() => setDialog({ kind: 'zone', zone: null })}>
        <Plus aria-hidden />
        {t('zone.create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name, currency: store.defaultCurrency })}
        actions={addButton('settings.shipping.add-zone')}
      />
      <ReadOnlyBanner reason={reason} />
      {query.isPending ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : zones.length === 0 ? (
        <EmptyState
          icon={Truck}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="settings.shipping.empty"
          tourLink
        />
      ) : (
        <div className="flex flex-col gap-4">
          {zones.map((zone, index) => (
            <ZoneCard
              key={zone.id}
              tour={index === 0}
              zone={zone}
              editable={editable}
              onZoneAction={onZoneAction}
              onRateAction={onRateAction}
            />
          ))}
        </div>
      )}
      {dialog.kind === 'zone' ? (
        <ZoneDialog
          key={dialog.zone?.id ?? 'new'}
          storeId={store.id}
          zones={zones}
          zone={dialog.zone}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      {dialog.kind === 'rate' ? (
        <RateDialog
          key={dialog.rate?.id ?? `new-${dialog.zone.id}`}
          storeId={store.id}
          currency={store.defaultCurrency}
          zone={dialog.zone}
          rate={dialog.rate}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      <DeleteShippingDialog
        open={dialog.kind === 'deleteZone'}
        onOpenChange={closeDialog}
        title={t('zone.deleteDialog.title', {
          name: dialog.kind === 'deleteZone' ? dialog.zone.name : '',
        })}
        description={t('zone.deleteDialog.description')}
        submitLabel={t('zone.deleteDialog.submit')}
        submittingLabel={t('zone.deleteDialog.submitting')}
        remove={deleteZone}
        targetId={dialog.kind === 'deleteZone' ? dialog.zone.id : null}
      />
      <DeleteShippingDialog
        open={dialog.kind === 'deleteRate'}
        onOpenChange={closeDialog}
        title={t('rate.deleteDialog.title', {
          name: dialog.kind === 'deleteRate' ? dialog.rate.name : '',
        })}
        description={t('rate.deleteDialog.description', {
          zone: dialog.kind === 'deleteRate' ? dialog.zone.name : '',
        })}
        submitLabel={t('rate.deleteDialog.submit')}
        submittingLabel={t('rate.deleteDialog.submitting')}
        remove={deleteRate}
        targetId={dialog.kind === 'deleteRate' ? dialog.rate.id : null}
      />
    </PageContent>
  );
}

export function ShippingPage() {
  return (
    <FeatureErrorBoundary name="shipping">
      <Shipping />
    </FeatureErrorBoundary>
  );
}
